import { useCallback, useMemo, useState } from 'react';
import { AsyncBoundary } from '@/components/feedback';
import { GrowthDashboardTopBarActions, MonthlyReportView } from '@elva/ui';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import {
  useMonthlyReport,
  useMonthlyReportMonths,
  useMonthlyReportMutations,
} from '../hooks/useMonthlyReport';

export function MonthlyReportPage() {
  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data: months, isPending: monthsPending } = useMonthlyReportMonths();
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();

  const activeMonth = useMemo(() => {
    if (selectedMonth) return selectedMonth;
    return months?.[0]?.value;
  }, [months, selectedMonth]);

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } =
    useMonthlyReport(activeMonth);
  const { exportPdf } = useMonthlyReportMutations();

  const handleExport = useCallback(() => {
    const reportId = data?.reportId;
    if (!reportId) return;
    exportPdf.mutate(reportId);
  }, [data?.reportId, exportPdf]);

  const availableMonths = useMemo(
    () =>
      months?.map((month) => ({
        label: month.label,
        value: month.value,
        status: month.status,
      })),
    [months],
  );

  return (
    <AsyncBoundary
      loading={isPending || monthsPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
    >
      <MonthlyReportView
        {...viewProps({
          data: data?.data,
          availableMonths,
          selectedMonth: activeMonth,
          onMonthChange: setSelectedMonth,
          generating: data?.generating,
          loading: isFetching || data?.generating,
          onExport: data?.reportId ? handleExport : undefined,
          exportPending: exportPdf.isPending,
        })}
      />
    </AsyncBoundary>
  );
}
